import React from 'react';
import HeaderFooterLayout from '../components/HeaderFooterLayout';
import { HelpCircle, Mail, ShieldCheck, Smartphone, Cloud } from 'lucide-react';
import { Link } from 'react-router-dom';

const FaqItem = ({ question, children }) => (
  <details className="p-4 rounded-lg bg-slate-50 dark:bg-zinc-800/50 group">
    <summary className="font-semibold cursor-pointer flex justify-between items-center text-gray-800 dark:text-white">
      {question}
      <span className="text-gray-400 group-open:rotate-90 transition-transform duration-200">&#x276F;</span>
    </summary>
    <div className="mt-4 text-gray-600 dark:text-gray-400">{children}</div>
  </details>
);

export default function SupportPage() {
  return (
    <HeaderFooterLayout>
      <main className="container mx-auto px-6 py-20">
        <div className="text-center mb-16">
          <HelpCircle size={48} className="mx-auto text-gray-700 dark:text-gray-300 mb-4" />
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 dark:text-white">Support Center</h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Find answers to common questions or contact our team for help with LumoChart, LumoDx, or other Lumosye products.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-12 items-start">
          {/* Contact */}
          <div className="bg-white dark:bg-zinc-900 p-10 rounded-3xl shadow-md border border-gray-100 dark:border-zinc-800">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Contact & Feedback</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
              For support or feedback, click below to open your default email app prefilled with our contact address.
            </p>
            <a
              href="mailto:contact@lumosyehealth.com?subject=Lumosye%20Support%20Request&body=Hello%20Lumosye%20Team%2C%0D%0A%0D%0A"
              className="flex items-center justify-center w-full bg-neutral-800 dark:bg-zinc-700 text-white font-semibold py-3 rounded-lg hover:bg-neutral-900 dark:hover:bg-zinc-600 transition shadow-md hover:shadow-lg"
            >
              <Mail size={20} className="mr-2" /> Email Support
            </a>

            <div className="mt-10 border-t border-gray-200 dark:border-zinc-800 pt-6 text-sm text-gray-600 dark:text-gray-400">
              <p><strong>Lumosye Health Services, LLC</strong><br />Houston, TX — United States</p>
              <p className="mt-2">
                📧{' '}
                <a href="mailto:contact@lumosyehealth.com" className="underline hover:text-gray-900 dark:hover:text-gray-200">
                  contact@lumosyehealth.com
                </a>
              </p>
            </div>
          </div>

          {/* FAQ */}
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Frequently Asked Questions</h2>
            <FaqItem question="Is Lumosye Health HIPAA compliant?">
              <p>Yes. Our infrastructure aligns with HIPAA standards using encryption and data minimization.</p>
            </FaqItem>
            <FaqItem question="Can I access my account across devices?">
              <p>Yes — your account syncs across iOS and web securely via cloud services.</p>
            </FaqItem>
            <FaqItem question="How do I report a bug or issue?">
              <p>
                Email us at{' '}
                <a href="mailto:contact@lumosyehealth.com" className="text-blue-600 dark:text-blue-400 hover:underline">
                  contact@lumosyehealth.com
                </a>{' '}
                with a description or screenshot.
              </p>
            </FaqItem>
            <FaqItem question="How long is clinical data retained?">
              <p>Data is deleted automatically after 15 days to uphold PHI minimization.</p>
            </FaqItem>
            <FaqItem question="Where can I learn more about Lumosye?">
              <p>
                Visit our <Link to="/" className="text-blue-600 dark:text-blue-400 hover:underline">main website</Link> to explore products.
              </p>
            </FaqItem>
          </div>
        </div>

        <div className="flex justify-center mt-24 space-x-10 text-gray-600 dark:text-gray-400">
          <ShieldCheck size={38} />
          <Smartphone size={38} />
          <Cloud size={38} />
        </div>
      </main>
    </HeaderFooterLayout>
  );
}
